import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabaseClient';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const run = async () => {
      const { data } = await supabase.auth.getSession();
      const session = data?.session;
      setHasSession(!!session);
      setIsReady(true);
    };

    run();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setHasSession(!!session);
    });

    return () => {
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const validate = () => {
    if (!password) return '새 비밀번호를 입력해주세요.';
    if (password.length < 8) return '비밀번호는 최소 8자 이상이어야 합니다.';
    if (password !== confirmPassword) return '비밀번호가 일치하지 않습니다.';
    return '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) {
        setError(updErr.message || '비밀번호 변경에 실패했습니다.');
        return;
      }

      alert('비밀번호가 변경되었습니다. 로그인해주세요.');
      await supabase.auth.signOut();
      navigate('/login-registration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-16">
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="max-w-lg mx-auto">
          <div className="bg-card rounded-lg shadow-gaming-md p-5 md:p-6 border border-border">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center">
                <Icon name="KeyRound" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-lg md:text-xl font-heading font-semibold text-foreground">비밀번호 재설정</div>
                <div className="text-xs md:text-sm text-muted-foreground caption mt-1">새 비밀번호를 설정하세요</div>
              </div>
            </div>

            {!isReady ? (
              <p className="text-sm text-muted-foreground caption">로딩 중...</p>
            ) : !hasSession ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground caption">
                  비밀번호 재설정 세션이 없습니다. 이메일로 받은 링크를 다시 열어주세요.
                </p>
                <Button variant="outline" onClick={() => navigate('/login-registration')}>로그인 화면으로</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Input
                    label="새 비밀번호"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="새 비밀번호를 입력하세요"
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    error={error}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-gaming"
                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </button>
                </div>

                <div className="relative">
                  <Input
                    label="새 비밀번호 확인"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="새 비밀번호를 다시 입력하세요"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-gaming"
                    aria-label={showConfirmPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </button>
                </div>

                {error && (
                  <p className="text-xs md:text-sm text-destructive">{error}</p>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button variant="outline" type="button" onClick={() => navigate('/login-registration')} disabled={isSaving}>
                    취소
                  </Button>
                  <Button type="submit" variant="default" loading={isSaving} iconName="Check" iconPosition="left">
                    변경
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
